import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../api';
import { leaveGuarded, sameValues, useUnsavedGuard, validForm } from '../ui/unsaved';
import { can, type Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import { HistoryPanel } from '../history/HistoryPanel';
import { FUNCTIONS_RESOURCE, PEOPLE_KEY, PERSONS_RESOURCE, type FunctionSummary, type Person } from '../people/types';
import { DuplicateNotice } from '../people/DuplicateNotice';
import { ConflictNotice } from '../ui/ConflictNotice';
import { RoleFormDialog, ROLES_KEY } from './RoleDialogs';
import type { AdminUser, Role } from './types';
import { Notice } from '../ui/Notice';

const USERS_KEY = ['admin', 'users'] as const;

function useRoles(enabled: boolean) {
  return useQuery({
    queryKey: ROLES_KEY,
    queryFn: () => api<{ roles: Role[] }>('/api/admin/roles').then((r) => r.roles),
    enabled,
  });
}

/** Role checkboxes (owner only) with a small "+ Nowa rola": the role is added in a dialog and ticked at once. */
function RoleChoice({ roles, value, onChange }: { roles: Role[]; value: string[]; onChange: (v: string[]) => void }) {
  const [adding, setAdding] = useState(false);
  return (
    <fieldset className="choices">
      <legend>Role</legend>
      {roles.map((role) => (
        <label key={role.id} className="check">
          <input
            type="checkbox"
            checked={value.includes(role.id)}
            onChange={(e) => onChange(e.target.checked ? [...value, role.id] : value.filter((id) => id !== role.id))}
          />
          <span>{role.label}</span>
        </label>
      ))}
      <button className="manage-link link-button" type="button" data-feature-id="identity.roles.manage" onClick={() => setAdding(true)}>
        + Nowa rola
      </button>
      {adding && (
        <RoleFormDialog
          roles={roles}
          onClose={() => setAdding(false)}
          onSaved={(role) => {
            setAdding(false);
            onChange([...value, role.id]);
          }}
        />
      )}
    </fieldset>
  );
}

function FieldError({ error, path }: { error: unknown; path: string }) {
  if (!(error instanceof ApiError)) return null;
  const issue = fieldIssues(error).find((i) => i.path === path);
  return issue ? <span className="field-error">{issue.message}</span> : null;
}

function GeneralError({ error }: { error: unknown }) {
  if (!(error instanceof ApiError) || error.status === 409) return null;
  const hasFieldIssues = error.error === 'validation' && fieldIssues(error).length > 0;
  return hasFieldIssues ? null : (
    <p className="alert" role="alert">
      {error.message}
    </p>
  );
}

/**
 * "Domyślnie widzi wszystkie wydarzenia" (spec 2026-10-23-widocznosc-wydarzen): only the owner changes it (it widens
 * what the account sees, like roles); others see it as text.
 */
function SeesAllEvents({ me, value, onChange }: { me: Me; value: boolean; onChange: (v: boolean) => void }) {
  if (!me.isOwner) return <p className="hint">{value ? 'Domyślnie widzi wszystkie wydarzenia.' : 'Widzi tylko wydarzenia, na których liście jest (albo które utworzył, prowadzi lub obsługuje).'}</p>;
  return (
    <label className="check">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <span>Domyślnie widzi wszystkie wydarzenia</span>
    </label>
  );
}

function CreateUserForm({ me, roles, onDone }: { me: Me; roles: Role[]; onDone: () => void }) {
  const queryClient = useQueryClient();
  // a new account sees only its own events unless the owner ticks "Domyślnie widzi wszystkie wydarzenia"
  const [form, setForm] = useState({ name: '', email: '', password: '', roleIds: [] as string[], seesAllEvents: false });
  const create = useMutation({
    mutationFn: () => {
      const { seesAllEvents, ...rest } = form;
      return api('/api/admin/users', { method: 'POST', body: me.isOwner ? { ...rest, seesAllEvents } : rest });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: USERS_KEY });
      onDone();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate();
  };
  // owner 2026-10-23 21:14: leaving with something typed asks first
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(form, { name: '', email: '', password: '', roleIds: [] }),
    save: async (el) => {
      if (!validForm(el)) return false;
      await create.mutateAsync();
      return true;
    },
  });
  return (
    <form ref={guardRef} className="panel form" onSubmit={submit} aria-label="Nowy użytkownik">
      <h2>Nowy użytkownik</h2>
      <GeneralError error={create.error} />
      <label className="field">
        <span>Imię i nazwisko</span>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoComplete="off" />
        <FieldError error={create.error} path="name" />
      </label>
      <label className="field">
        <span>E-mail</span>
        <input
          type="email"
          inputMode="email"
          autoCapitalize="none"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
          autoComplete="off"
        />
        <FieldError error={create.error} path="email" />
      </label>
      <label className="field">
        <span>Hasło początkowe</span>
        <input
          type="password"
          minLength={10}
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          required
          autoComplete="new-password"
        />
        <small className="hint">Co najmniej 10 znaków.</small>
        <FieldError error={create.error} path="password" />
      </label>
      {me.isOwner && <RoleChoice roles={roles} value={form.roleIds} onChange={(roleIds) => setForm({ ...form, roleIds })} />}
      {me.isOwner && <SeesAllEvents me={me} value={form.seesAllEvents} onChange={(seesAllEvents) => setForm({ ...form, seesAllEvents })} />}
      <div className="actions">
        <button className="btn primary" type="submit" disabled={create.isPending}>
          {create.isPending ? 'Dodawanie…' : 'Dodaj użytkownika'}
        </button>
        <button className="btn" type="button" onClick={onDone}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

function EditUserForm({ user, me, roles, onDone }: { user: AdminUser; me: Me; roles: Role[]; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [base, setBase] = useState(user);
  const [draft, setDraft] = useState({ name: user.name, active: user.active, roleIds: user.roleIds, seesAllEvents: user.seesAllEvents });
  const isSelf = user.id === me.id;
  const save = useMutation({
    mutationFn: () =>
      api<AdminUser>(`/api/admin/users/${user.id}`, {
        method: 'PATCH',
        body: { version: base.version, name: draft.name, active: draft.active, ...(me.isOwner ? { roleIds: draft.roleIds, seesAllEvents: draft.seesAllEvents } : {}) },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: USERS_KEY });
      onDone();
    },
  });
  const conflict = save.error instanceof ApiError && save.error.status === 409;

  async function reload() {
    // Take the server's current state as the new baseline; the person re-applies their change.
    const current = (save.error as ApiError).current as AdminUser | undefined;
    const fresh = current ?? (await api<{ users: AdminUser[] }>('/api/admin/users')).users.find((u) => u.id === user.id);
    if (fresh) {
      setBase(fresh);
      setDraft({ name: fresh.name, active: fresh.active, roleIds: fresh.roleIds, seesAllEvents: fresh.seesAllEvents });
    }
    save.reset();
    await queryClient.invalidateQueries({ queryKey: USERS_KEY });
  }

  const guardRef = useUnsavedGuard({
    dirty: !sameValues({ ...draft, roleIds: [...draft.roleIds].sort() }, { name: base.name, active: base.active, roleIds: [...base.roleIds].sort() }),
    save: async (el) => {
      if (!validForm(el)) return false;
      await save.mutateAsync();
      return true;
    },
  });

  return (
    <form
      ref={guardRef}
      className="edit"
      aria-label={`Edycja: ${user.name}`}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {conflict && <ConflictNotice onReload={() => void reload()} />}
      <GeneralError error={save.error} />
      <label className="field">
        <span>Imię i nazwisko</span>
        <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required />
        <FieldError error={save.error} path="name" />
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={draft.active}
          disabled={isSelf}
          onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
        />
        <span>Konto aktywne{isSelf ? ' (własnego konta nie można dezaktywować)' : ''}</span>
      </label>
      {me.isOwner ? (
        <RoleChoice roles={roles} value={draft.roleIds} onChange={(roleIds) => setDraft({ ...draft, roleIds })} />
      ) : (
        <p className="hint">Role może zmieniać tylko właściciel.</p>
      )}
      {!user.isOwner && <SeesAllEvents me={me} value={draft.seesAllEvents} onChange={(seesAllEvents) => setDraft({ ...draft, seesAllEvents })} />}
      <div className="actions">
        <button className="btn primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Zapisywanie…' : 'Zapisz zmiany'}
        </button>
        <button className="btn" type="button" onClick={onDone}>
          Anuluj
        </button>
        {me.isOwner && !user.isOwner && (
          <Link className="btn ghost" to={`/uzytkownicy/${user.id}/uprawnienia`}>
            Uprawnienia indywidualne
          </Link>
        )}
      </div>
    </form>
  );
}

function ResetPasswordForm({ user, onDone }: { user: AdminUser; onDone: (message: string) => void }) {
  const [password, setPassword] = useState('');
  const reset = useMutation({
    mutationFn: () => api(`/api/admin/users/${user.id}/password`, { method: 'POST', body: { newPassword: password } }),
    onSuccess: () => onDone(`Ustawiono nowe hasło dla: ${user.name}. Wszystkie jego sesje zostały zakończone.`),
  });
  const guardRef = useUnsavedGuard({
    dirty: password !== '',
    save: async (el) => {
      if (!validForm(el)) return false;
      await reset.mutateAsync();
      return true;
    },
  });
  return (
    <form
      ref={guardRef}
      className="edit"
      aria-label={`Nowe hasło: ${user.name}`}
      data-feature-id="identity.users.password_reset"
      onSubmit={(e) => {
        e.preventDefault();
        reset.mutate();
      }}
    >
      <GeneralError error={reset.error} />
      <label className="field">
        <span>Nowe hasło</span>
        <input
          type="password"
          minLength={10}
          required
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <small className="hint">Co najmniej 10 znaków. Przekaż je osobie bezpiecznym kanałem; użytkownik zostanie wylogowany ze wszystkich urządzeń.</small>
        <FieldError error={reset.error} path="newPassword" />
      </label>
      <div className="actions">
        <button className="btn primary" type="submit" disabled={reset.isPending}>
          {reset.isPending ? 'Zapisywanie…' : 'Ustaw hasło'}
        </button>
        <button className="btn" type="button" onClick={() => onDone('')}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

/** "Funkcje: Technik, Kierowca · Zmień funkcje" — or, for an account without a person, "Dodaj jako osobę". */
function PersonLine({ user, person, functionNames, mayNewPerson }: { user: AdminUser; person: Person | undefined; functionNames: (p: Person) => string[]; mayNewPerson: boolean }) {
  if (person) {
    const names = functionNames(person);
    return (
      <>
        <div className="muted">
          Funkcje: {names.length > 0 ? names.join(', ') : 'brak'} · <Link to={`/osoby?osoba=${person.id}`}>Zmień funkcje</Link>
        </div>
        <DuplicateNotice person={person} />
      </>
    );
  }
  if (!mayNewPerson) return null;
  return (
    <div className="muted">
      Brak osoby z funkcjami · <Link to={`/osoby?konto=${user.id}`}>Dodaj jako osobę</Link>
    </div>
  );
}

export function UsersPage() {
  const { me } = useMe();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [passwordId, setPasswordId] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const users = useQuery({ queryKey: USERS_KEY, queryFn: () => api<{ users: AdminUser[] }>('/api/admin/users').then((r) => r.users) });
  const roles = useRoles(Boolean(me?.isOwner));
  // what each account can do in the crew lives on its person (Osoby): shown here with a way to change it
  const seePeople = !!me && can(me, PERSONS_RESOURCE, 'view') && can(me, FUNCTIONS_RESOURCE, 'view');
  const persons = useQuery({ queryKey: [...PEOPLE_KEY, 'persons', 'by-account'], enabled: seePeople, queryFn: () => api<{ persons: Person[] }>('/api/people/persons') });
  const functions = useQuery({ queryKey: [...PEOPLE_KEY, 'functions'], enabled: seePeople, queryFn: () => api<{ functions: FunctionSummary[] }>('/api/people/functions') });
  if (!me) return null;
  const personOf = (userId: string) => persons.data?.persons.find((p) => p.userId === userId);
  /** The person's functions in the dictionary's order. */
  const functionsOf = (p: Person) => {
    const ids = new Set((p.functions ?? []).map((f) => f.functionId));
    return (functions.data?.functions ?? []).filter((f) => ids.has(f.id)).map((f) => f.name ?? 'Funkcja');
  };
  // „Kto widzi” (review I1): only the owner links an account with a person — a person made by anyone else would lose the link
  const mayNewPerson = me.isOwner && can(me, PERSONS_RESOURCE, 'create');
  const roleList = roles.data ?? [];
  const roleLabel = (id: string) => roleList.find((r) => r.id === id)?.label ?? 'Rola';

  return (
    <section className="page" data-feature-id="identity.users.manage">
      <div className="page-head">
        <h1>Użytkownicy</h1>
        {can(me, 'identity.users', 'create') && !creating && (
          <button className="btn primary" type="button" onClick={() => setCreating(true)}>
            Dodaj użytkownika
          </button>
        )}
      </div>

      {creating && <CreateUserForm me={me} roles={roleList} onDone={() => setCreating(false)} />}

      {notice && (
        <Notice onClose={() => setNotice('')}>
          {notice}
        </Notice>
      )}

      {users.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {users.error && (
        <p className="alert" role="alert">
          {users.error instanceof ApiError ? users.error.message : 'Nie udało się wczytać użytkowników'}
        </p>
      )}

      <ul className="list">
        {users.data?.map((user) => {
          const editable = can(me, 'identity.users', 'edit') && (me.isOwner || !user.isOwner);
          const open = editingId === user.id;
          const resetting = passwordId === user.id;
          const canResetPassword = me.isOwner && !user.isOwner;
          return (
            <li key={user.id} className={user.active ? 'row' : 'row inactive'}>
              <div className="row-main">
                <div className="row-title">
                  <strong>{user.name}</strong>
                  {user.isOwner && <span className="chip owner">Właściciel</span>}
                  {!user.active && <span className="chip off">Nieaktywny</span>}
                  {!user.isOwner && user.seesAllEvents && <span className="chip">Widzi wszystkie wydarzenia</span>}
                </div>
                <div className="muted">{user.email}</div>
                {seePeople && persons.data && functions.data && (
                  <PersonLine user={user} person={personOf(user.id)} functionNames={functionsOf} mayNewPerson={mayNewPerson} />
                )}
                {user.roleIds.length > 0 && (
                  <div className="chips">
                    {user.roleIds.map((id) => (
                      <span key={id} className="chip">
                        {me.isOwner ? roleLabel(id) : 'Rola'}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {!open && !resetting && (
                <div className="actions row-actions">
                  {editable && (
                    <button
                      className="btn small"
                      type="button"
                      onClick={() =>
                        leaveGuarded(() => {
                          setNotice('');
                          setEditingId(user.id);
                        })
                      }
                      aria-label={`Edytuj: ${user.name}`}
                    >
                      Edytuj
                    </button>
                  )}
                  {canResetPassword && (
                    <button
                      className="btn small"
                      type="button"
                      data-feature-id="identity.users.password_reset"
                      onClick={() =>
                        leaveGuarded(() => {
                          setNotice('');
                          setPasswordId(user.id);
                        })
                      }
                      aria-label={`Ustaw nowe hasło: ${user.name}`}
                    >
                      Ustaw nowe hasło
                    </button>
                  )}
                </div>
              )}
              {resetting && (
                <ResetPasswordForm
                  user={user}
                  onDone={(message) => {
                    setPasswordId(null);
                    setNotice(message);
                  }}
                />
              )}
              {open && <EditUserForm user={user} me={me} roles={roleList} onDone={() => setEditingId(null)} />}
              {open && can(me, 'history.entries', 'view') && <HistoryPanel entityId={user.id} resource="identity.users" />}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
